import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A hook to persist tab state in the URL query parameters.
 * 
 * @param {string} defaultTab - The default tab to show if no param is present
 * @param {string} paramName - The name of the query parameter (default: 'tab')
 * @returns {[string, Function]} - [activeTab, setActiveTab]
 */
const usePersistedTab = (defaultTab, paramName = 'tab') => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize state from URL param or default
    const initialTab = searchParams.get(paramName) || defaultTab;
    const [activeTab, setActiveTabState] = useState(initialTab);

    // Sync state with URL when URL changes (e.g. back button)
    useEffect(() => {
        const currentTab = searchParams.get(paramName);
        if (currentTab && currentTab !== activeTab) {
            setActiveTabState(currentTab);
        } else if (!currentTab && activeTab !== defaultTab) {
            // If param is removed but we're not on default, reset to default
            // Or we could choose to keep current state. 
            // Let's stick to syncing with URL: if URL has no tab, it implies default.
            setActiveTabState(defaultTab);
        }
    }, [searchParams, paramName, defaultTab, activeTab]);

    // Function to update both state and URL
    const setActiveTab = (newTab) => {
        setActiveTabState(newTab);

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (newTab === defaultTab) {
            newParams.delete(paramName);
        } else {
            newParams.set(paramName, newTab);
        }
        setSearchParams(newParams, { replace: true });
    };

    return [activeTab, setActiveTab];
};

export default usePersistedTab;
